import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { customerApi } from '@/lib/customerApi';

const KEY = 'ei-cart-v1';
const CartContext = createContext(null);

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
};

const sameLine = (a, b) => a.id === b.id && a.kind === b.kind;

export const CartProvider = ({ children }) => {
  const [lines, setLines] = useState(read);
  const [isOpen, setIsOpen] = useState(false);
  const { customer } = useCustomerAuth();
  const mergedFor = useRef(null);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(lines)); }, [lines]);

  // On sign-in, merge whatever is in this browser with the cart saved against the account.
  useEffect(() => {
    if (!customer || mergedFor.current === customer.id) return;
    mergedFor.current = customer.id;
    let live = true;
    (async () => {
      try {
        const { data } = await customerApi.get('/account/cart');
        if (!live) return;
        setLines((current) => {
          const merged = [...(data.lines || [])];
          current.forEach((line) => {
            const found = merged.find((item) => sameLine(item, line));
            if (found) found.quantity = Math.min(50, Math.max(found.quantity, line.quantity));
            else merged.push(line);
          });
          return merged;
        });
      } catch (e) { console.warn("Saved basket could not be merged; using the local basket", e); }
    })();
    return () => { live = false; };
  }, [customer]);

  // Push every change back to the account so it is there on the next device.
  useEffect(() => {
    if (!customer) { mergedFor.current = null; return; }
    if (mergedFor.current !== customer.id) return;
    const timer = setTimeout(() => {
      customerApi.put('/account/cart', { lines: lines.map(({ kind, id, quantity }) => ({ kind, id, quantity })) }).catch(() => {});
    }, 600);
    return () => clearTimeout(timer);
  }, [lines, customer]);

  const add = useCallback((line) => {
    setLines((current) => {
      const found = current.find((item) => sameLine(item, line));
      if (found) return current.map((item) => (item === found ? { ...item, quantity: Math.min(50, item.quantity + (line.quantity || 1)) } : item));
      return [...current, { kind: line.kind, id: line.id, name: line.name, image: line.image || null, includes: line.includes || null, quantity: line.quantity || 1 }];
    });
    setIsOpen(true);
  }, []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const setQuantity = useCallback((id, kind, quantity) => setLines((current) => current.map((item) => (item.id === id && item.kind === kind ? { ...item, quantity: Math.max(1, Math.min(50, Number(quantity) || 1)) } : item))), []);
  const remove = useCallback((id, kind) => setLines((current) => current.filter((item) => !(item.id === id && item.kind === kind))), []);
  const clear = useCallback(() => setLines([]), []);
  const count = useMemo(() => lines.reduce((sum, item) => sum + item.quantity, 0), [lines]);

  return <CartContext.Provider value={{ lines, add, setQuantity, remove, clear, count, isOpen, open, close }}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
